import { Editor } from "@monaco-editor/react";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import PauseIcon from "@mui/icons-material/Pause";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { theme } from "@/theme/theme";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useAutoScroll } from "../hooks/use-auto-scroll.tsx";
import type { EventCreateSchema } from "../types/events.ts";
import type { ApplicationAnalysisDoneIntent, TestAgentV2Session } from "../types/test-agent-2.ts";
import { formatDuration } from "../utils";
import { humanizeText } from "../utils/text.ts";
import { HistoryItemWrapper } from "./history-item-wrapper.tsx";
import { MarkdownBlock } from "./markdown-block.tsx";
import { VideoPlayer } from "./video-player.tsx";

interface SessionCardProps {
  sessionId: string;
  path?: string;
  eventsPath?: string;
  defaultExpanded?: boolean;
  onExpand?: (expanded: boolean) => void;
}

function getStatusColor(status: string) {
  switch (status) {
    case "running":
      return "primary";
    case "closed":
      return "success";
    case "paused":
      return "warning";
    case "need_user_input":
      return "info";
    default:
      return "default";
  }
}

function getStatusText(status: string) {
  switch (status) {
    case "running":
      return "running";
    case "closed":
      return "completed";
    case "paused":
      return "paused";
    case "need_user_input":
      return "needs input";
    case "idle":
      return "idle";
    case "pending":
      return "pending";
    default:
      return status;
  }
}

export function SessionCard({
  sessionId,
  path,
  eventsPath,
  defaultExpanded,
  onExpand,
}: SessionCardProps) {
  const { data: session, isLoading } = useApiQuery<TestAgentV2Session>(
    `/${path ?? "test-agent-v2-sessions"}/${sessionId}`,
    {
      enabled: !!sessionId,
      refetchInterval: (data) => {
        if (data.state.data?.data.status === "closed") {
          return false;
        }

        return 5000;
      },
    },
  );

  const [response, setResponse] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: newEvent, isPending: isCreatingEvent } = useApiMutation<EventCreateSchema>(
    "POST",
    `/${eventsPath ?? "new-event"}`,
    {
      onSuccess: () => {
        setResponse("");
      },
    },
  );

  const { mutate: sendSessionEvent, isPending: isSendingEvent } = useApiMutation<EventCreateSchema>(
    "POST",
    `/${eventsPath ?? "new-event"}`,
    {
      onSuccess: (_, variables) => {
        const eventType = variables.event_data?.type;
        if (eventType === "close") {
          enqueueSnackbar("Session cancellation request sent", {
            variant: "warning",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          });
        } else if (eventType === "toggle_autopilot") {
          enqueueSnackbar("YOLO mode toggled", {
            variant: "info",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          });
        }
      },
      onError: (error, variables) => {
        const eventType = variables.event_data?.type;
        if (eventType === "close") {
          enqueueSnackbar(`Failed to cancel session: ${error.message}`, {
            variant: "error",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          });
        } else if (eventType === "toggle_autopilot") {
          enqueueSnackbar(`Failed to toggle YOLO mode: ${error.message}`, {
            variant: "error",
            anchorOrigin: {
              horizontal: "right",
              vertical: "bottom",
            },
          });
        }
      },
    },
  );

  const [recordingPath, setRecordingPath] = useState<string | null>(null);

  useEffect(() => {
    if (session?.data.recording_path && !recordingPath) {
      setRecordingPath(session.data.recording_path);
    }
  }, [session?.data.recording_path, recordingPath]);

  const firstTask = useMemo(() => {
    return session?.data.history?.[0]?.task;
  }, [session?.data.history]);

  const url = useMemo(() => {
    const liveViewUrl = session?.data.liveview_url;

    if (!liveViewUrl) {
      return null;
    }

    const url = new URL(
      liveViewUrl.includes("liveview-2")
        ? liveViewUrl
        : liveViewUrl.replace("liveview", "liveview-2"),
    );

    url.searchParams.set("hideControls", "true");

    return url.toString();
  }, [session?.data.liveview_url]);

  const historyScrollRef = useRef<HTMLDivElement>(null);
  useAutoScroll(historyScrollRef.current, session?.data.history.length);

  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);

  useEffect(() => {
    if (onExpand) onExpand(expanded);
  }, [expanded, onExpand]);

  const elapsed_seconds = useMemo(() => {
    const total =
      session?.data.history?.reduce((acc, item) => acc + (item.elapsed_ms || 0), 0) || 0;
    return Math.floor(total / 1000);
  }, [session?.data.history]);

  const doneIntentType = useMemo(() => {
    return session?.data.done_intent || "test_creator";
  }, [session?.data.done_intent]);

  const analysisResult = useMemo(() => {
    if (doneIntentType === "app_analysis" && session?.data.last_done) {
      const analysisDone = session.data.last_done as ApplicationAnalysisDoneIntent;
      return {
        discovered_elements: analysisDone.discovered_elements || [],
        coverage_analysis: analysisDone.coverage_analysis || "",
      };
    }
    return null;
  }, [doneIntentType, session?.data.last_done]);

  const sessionTypeName = useMemo(() => {
    if (session?.data.name) {
      return session.data.name;
    }

    switch (doneIntentType) {
      case "app_analysis":
        return "Application Analysis";
      case "test_creator":
        return "Test Creator";
      case "next_qa_task":
        return "QA Task";
      case "extraction":
        return "Data Extraction";
      default:
        return "";
    }
  }, [session?.data.name, doneIntentType]);

  if (isLoading) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Stack alignItems="center" justifyContent="center" height={200}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary" mt={1}>
              Loading session...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!session) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Stack alignItems="center" justifyContent="center" height={200}>
            <Typography variant="body2" color="text.secondary" align="center">
              Session not found
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const inner = (
    <Card sx={{ height: "100%", border: "none", minWidth: "300px" }}>
      <CardContent sx={{ p: 2 }}>
        <Stack direction="column" spacing={expanded ? 2 : 1} width="100%" divider={<Divider />}>
          {/* Session type/name indicator */}
          {sessionTypeName && (
            <Stack direction="row" alignItems="center" width="100%">
              <Chip label={sessionTypeName} size="small" variant="outlined" sx={{ opacity: 0.7 }} />
            </Stack>
          )}

          {/* Header with status aligned to the right */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            spacing={1}
          >
            {firstTask && (
              <Tooltip title={firstTask}>
                <Typography variant="body2" color="text.primary" noWrap sx={{ maxWidth: "600px" }}>
                  {firstTask}
                </Typography>
              </Tooltip>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={humanizeText(getStatusText(session.data.status))}
                color={getStatusColor(session.data.status)}
                size="small"
                variant="filled"
                icon={
                  session.data.status === "running" ? (
                    <CircularProgress size={10} color="inherit" />
                  ) : undefined
                }
              />

              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="small"
                title={expanded ? "Collapse" : "Expand"}
              >
                {expanded ? (
                  <CloseFullscreenIcon fontSize="small" />
                ) : (
                  <OpenInFullIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>
          </Stack>

          <Grid container spacing={{ xs: 2 }}>
            <Grid
              size={{
                xs: expanded ? 12 : 12,
                md: expanded ? 6 : 12,
                lg: expanded ? 8 : 12,
              }}
            >
              {/* Live view preview */}
              <Stack direction="column" spacing={1} width="100%">
                {session.data.status !== "closed" && url && (
                  <iframe
                    src={url}
                    sandbox="allow-same-origin allow-scripts"
                    allow="clipboard-read; clipboard-write"
                    style={{
                      width: "100%",
                      height: expanded ? "520px" : "220px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                    title="Live View"
                  />
                )}
                {session.data.status === "closed" && recordingPath && (
                  <VideoPlayer
                    src={recordingPath}
                    width="100%"
                    height={expanded ? 520 : 220}
                    controls={true}
                    autoPlay={true}
                    muted={true}
                  />
                )}
                {!recordingPath && !url && (
                  <Stack alignItems="center" justifyContent="center" height="100%" spacing={1}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {session.data.status === "running" ? "Loading..." : "No preview available"}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: expanded ? 6 : 12, lg: expanded ? 4 : 12 }}>
              <Stack
                direction="column"
                spacing={expanded ? 2 : 1}
                alignItems="stretch"
                width="100%"
                divider={<Divider />}
              >
                <Stack
                  direction="column"
                  spacing={2}
                  divider={<Divider />}
                  sx={{
                    mt: 1,
                    maxHeight: expanded ? 420 : 200,
                    overflowY: "auto",
                    width: "100%",
                  }}
                  ref={historyScrollRef}
                >
                  {session.data.history.map((item, index) => (
                    <HistoryItemWrapper
                      key={index}
                      history={item}
                      blocks={[]}
                      isLast={index === session.data.history.length - 1}
                    />
                  ))}
                  {session.data.history.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No logs yet...
                    </Typography>
                  )}
                </Stack>
                {session.data.status === "running" && (
                  <Stack direction="column" spacing={1} width="100%">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      {/* Pause, Cancel and YOLO Mode Controls */}
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Pause Session">
                          <IconButton
                            size="small"
                            color="inherit"
                            disabled={isCreatingEvent}
                            onClick={() => {
                              newEvent({
                                group_key: session.id,
                                event_type: "test_agent_2.event.incoming",
                                event_data: {
                                  type: "pause",
                                },
                              });
                            }}
                            sx={{
                              opacity: 0.5,
                              "&:hover": { opacity: 0.8 },
                              "&:disabled": { opacity: 0.2 },
                            }}
                          >
                            <PauseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel Session">
                          <IconButton
                            size="small"
                            color="warning"
                            disabled={isSendingEvent}
                            onClick={() => {
                              sendSessionEvent({
                                group_key: session.id,
                                event_type: "test_agent_2.event.incoming",
                                event_data: {
                                  type: "close",
                                },
                              });
                            }}
                            sx={{
                              opacity: 0.5,
                              "&:hover": { opacity: 0.8 },
                              "&:disabled": { opacity: 0.2 },
                            }}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={session.data.autopilot ?? true}
                              disabled={isSendingEvent}
                              onChange={() => {
                                sendSessionEvent({
                                  group_key: session.id,
                                  event_type: "test_agent_2.event.incoming",
                                  event_data: {
                                    type: "toggle_autopilot",
                                  },
                                });
                              }}
                              sx={{
                                opacity: 0.6,
                                "&:hover": { opacity: 0.8 },
                                "&:disabled": { opacity: 0.3 },
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.65rem",
                                opacity: 0.6,
                                minWidth: "35px",
                                fontWeight: 400,
                              }}
                            >
                              YOLO
                            </Typography>
                          }
                          sx={{ margin: 0, marginLeft: 0.5 }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
                {["pending", "need_user_input", "paused"].includes(session.data.status) && (
                  <Stack direction="column" spacing={1} width="100%">
                    <TextField
                      label="Reply to the agent..."
                      variant="outlined"
                      size="small"
                      fullWidth
                      placeholder="Shift + Enter for new line, Enter to send"
                      type="text"
                      multiline
                      minRows={1}
                      maxRows={3}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => {
                                  if (response.trim().length === 0 || isCreatingEvent) {
                                    return;
                                  }

                                  newEvent({
                                    group_key: session.id,
                                    event_type: "test_agent_2.event.incoming",
                                    event_data: {
                                      type: "response",
                                      message: response.trim(),
                                    },
                                  });
                                }}
                                edge="end"
                                size="small"
                                disabled={isCreatingEvent || response.trim().length === 0}
                                sx={{
                                  color: "black",
                                  opacity: 0.6,
                                  "&:hover": { opacity: 0.9 },
                                  "&:disabled": { opacity: 0.3 },
                                }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();

                          if (response.trim().length === 0 || isCreatingEvent) {
                            return;
                          }

                          newEvent({
                            group_key: session.id,
                            event_type: "test_agent_2.event.incoming",
                            event_data: {
                              type: "response",
                              message: response.trim(),
                            },
                          });
                        }
                      }}
                    />

                    {/* Cancel and YOLO Mode Controls */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <Tooltip title="Cancel Session">
                        <IconButton
                          size="small"
                          color="warning"
                          disabled={isSendingEvent}
                          onClick={() => {
                            sendSessionEvent({
                              group_key: session.id,
                              event_type: "test_agent_2.event.incoming",
                              event_data: {
                                type: "close",
                              },
                            });
                          }}
                          sx={{
                            opacity: 0.5,
                            "&:hover": { opacity: 0.8 },
                            "&:disabled": { opacity: 0.2 },
                          }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={session.data.autopilot ?? true}
                            disabled={isSendingEvent}
                            onChange={() => {
                              sendSessionEvent({
                                group_key: session.id,
                                event_type: "test_agent_2.event.incoming",
                                event_data: {
                                  type: "toggle_autopilot",
                                },
                              });
                            }}
                            sx={{
                              opacity: 0.8,
                              "&:hover": { opacity: 1 },
                              "&:disabled": { opacity: 0.3 },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.65rem",
                              opacity: 0.6,
                              minWidth: "35px",
                              fontWeight: 400,
                            }}
                          >
                            YOLO
                          </Typography>
                        }
                        sx={{ margin: 0, marginLeft: 0.5 }}
                      />
                    </Stack>
                  </Stack>
                )}
                {session.data.status === "closed" && (
                  <Stack direction="column" spacing={1} width="100%">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Stack direction="column" spacing={0} alignItems="flex-start">
                        <Typography variant="caption" noWrap>
                          {humanizeText(session.data.last_done?.status ?? "Error")} after{" "}
                          {session.data.history.length} steps and {session.data.blocks.length}{" "}
                          actions
                        </Typography>
                        <Typography variant="caption" noWrap color="text.secondary">
                          Took {formatDuration(elapsed_seconds)}
                        </Typography>
                      </Stack>
                      {doneIntentType === "test_creator" && session.data.test_id && (
                        <Box sx={{ cursor: "pointer" }}>
                          <Link
                            href={`/tests-main/tests/${session.data.test_id}`}
                            target="_blank"
                            variant="caption"
                          >
                            View Test
                          </Link>
                        </Box>
                      )}

                      {doneIntentType === "app_analysis" && analysisResult && (
                        <Box sx={{ cursor: "pointer", minWidth: "80px" }}>
                          <Link
                            component="button"
                            variant="caption"
                            onClick={() => setAnalysisDialogOpen(true)}
                          >
                            View Analysis
                          </Link>
                        </Box>
                      )}
                    </Stack>
                    {doneIntentType === "discovery_prompt" && (
                      <Stack direction="column" spacing={1}>
                        <MarkdownBlock
                          content={
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (session.data.last_done as any)?.intent || ""
                          }
                        />
                        <Editor
                          height="400px"
                          defaultLanguage="json"
                          value={
                            session.data.last_done
                              ? JSON.stringify(session.data.last_done, null, 2)
                              : ""
                          }
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                            wordWrap: "on",
                            scrollBeyondLastLine: false,
                          }}
                        />
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );

  const analysisDialog = analysisResult && (
    <Dialog
      open={analysisDialogOpen}
      onClose={() => setAnalysisDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Typography variant="h6">Application Analysis Results</Typography>

          {analysisResult.coverage_analysis && (
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle2" fontWeight="600">
                Coverage Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                {analysisResult.coverage_analysis}
              </Typography>
            </Stack>
          )}

          {analysisResult.discovered_elements.length > 0 && (
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle2" fontWeight="600">
                Discovered Elements ({analysisResult.discovered_elements.length})
              </Typography>
              <Stack direction="column" spacing={1.5}>
                {analysisResult.discovered_elements.map((element, idx) => (
                  <Card
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Stack direction="column" spacing={0.75}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={humanizeText(element.element_type)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Typography variant="body2" fontWeight="600">
                          {element.name}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {element.description}
                      </Typography>
                      {element.url && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {element.url}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );

  if (expanded) {
    return (
      <>
        {analysisDialog}
        <Dialog open={true} onClose={() => setExpanded(false)} maxWidth="xl" fullWidth>
          <DialogContent sx={{ p: 0 }}>{inner}</DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {analysisDialog}
      {inner}
    </>
  );
}

export default SessionCard;
